import { useState, useCallback } from "react";
import {
  Box, Sheet, Card, Typography, Input, Button, IconButton, Chip, Alert,
  CircularProgress, Divider, Select, Option, Table, Tabs, TabList, Tab, TabPanel, Tooltip, Checkbox,
} from "@mui/joy";
import { motion } from "framer-motion";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { adminApi } from "../adminApi";

const LEVEL_COLOR = { error: "danger", warn: "warning", info: "primary", debug: "neutral" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("ru-RU") : "—");
const short = (s, n = 8) => (s ? String(s).slice(0, n) : "—");

// Ссылка для входа под клиентом + флаги «умной ссылки» (?fit_image / ?converter),
// которые включают для этого клиента сжатие фото и конвертер HEIC.
function buildClientLink(base, compress, convert) {
  if (!base) return base;
  const flags = [];
  if (compress) flags.push("fit_image");
  if (convert) flags.push("converter");
  if (!flags.length) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}${flags.join("&")}`;
}

// ── Плитка-счётчик ──
function StatTile({ label, value, color }) {
  return (
    <Sheet variant="soft" color={color} sx={{ flex: 1, minWidth: 120, borderRadius: "12px", p: 1.5 }}>
      <Typography level="h2" sx={{ lineHeight: 1 }}>{value ?? 0}</Typography>
      <Typography level="body-xs">{label}</Typography>
    </Sheet>
  );
}

// ── Пагинатор блока ──
function Pager({ block, onPage }) {
  if (!block || block.num_pages <= 1) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
      <Button size="sm" variant="outlined" color="neutral" disabled={!block.has_prev}
              onClick={() => onPage(block.page - 1)}>← назад</Button>
      <Typography level="body-xs" color="neutral">стр. {block.page} из {block.num_pages}</Typography>
      <Button size="sm" variant="outlined" color="neutral" disabled={!block.has_next}
              onClick={() => onPage(block.page + 1)}>вперёд →</Button>
    </Box>
  );
}

function LogRow({ log }) {
  const [open, setOpen] = useState(false);
  const hasPayload = log.payload && Object.keys(log.payload).length > 0;
  return (
    <>
      <tr>
        <td>
          <Typography level="body-xs">{fmtDate(log.client_ts || log.created_at)}</Typography>
          <Typography level="body-xs" color="neutral">{fmtDate(log.created_at)}</Typography>
        </td>
        <td><Chip size="sm" variant="soft" color={LEVEL_COLOR[log.level] || "neutral"}>{log.level}</Chip></td>
        <td>
          {log.source === "qc"
            ? <Chip size="sm" variant="soft" color="warning">QC</Chip>
            : <Typography level="body-xs">{log.source_display || log.source}</Typography>}
        </td>
        <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{log.event}</Typography></td>
        <td>
          <Typography level="body-sm">{log.message}</Typography>
          {log.url && <Typography level="body-xs" color="neutral" sx={{ wordBreak: "break-all" }}>{log.url}</Typography>}
          {hasPayload && (
            <Button size="sm" variant="plain" color="neutral" sx={{ mt: 0.5, minHeight: 0, py: 0.25 }}
                    endDecorator={<KeyboardArrowDownRoundedIcon sx={{ transform: open ? "rotate(180deg)" : "none", transition: ".2s" }} />}
                    onClick={() => setOpen((v) => !v)}>
              payload
            </Button>
          )}
        </td>
      </tr>
      {open && hasPayload && (
        <tr>
          <td colSpan={5} style={{ background: "transparent" }}>
            <Box component="pre" sx={{
              m: 0, p: 1.5, borderRadius: "8px", bgcolor: "#1e1e1e", color: "#d4d4d4",
              fontSize: 12, overflow: "auto", maxHeight: 320,
            }}>
              {JSON.stringify(log.payload, null, 2)}
            </Box>
          </td>
        </tr>
      )}
    </>
  );
}

export default function LogsPage() {
  const [codeInput, setCodeInput] = useState("");
  const [bookInput, setBookInput] = useState("");
  const [orderInput, setOrderInput] = useState("");
  const [level, setLevel] = useState("");
  const [source, setSource] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pages, setPages] = useState({ books_page: 1, orders_page: 1, processes_page: 1, logs_page: 1 });
  const [linkCompress, setLinkCompress] = useState(false);
  const [linkConvert, setLinkConvert] = useState(false);

  // активный «ключ» запроса — что реально ищем (book важнее code)
  const activeParams = useCallback((overrides = {}) => {
    const p = { level, source, ...pages, ...overrides };
    const book = (overrides.book ?? bookInput).trim();
    const order = (overrides.order ?? orderInput).trim();
    const code = (overrides.code ?? codeInput).trim();
    if (book) p.book = book;
    else if (order) p.order = order;
    else if (code) p.code = code;
    return p;
  }, [codeInput, bookInput, orderInput, level, source, pages]);

  const run = useCallback(async (params) => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.logsLookup(params);
      setData(res);
    } catch (e) {
      setError(e.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, []);

  const onSearch = () => {
    const reset = { books_page: 1, orders_page: 1, processes_page: 1, logs_page: 1 };
    setPages(reset);
    run(activeParams(reset));
  };

  const onFilter = (patch) => {
    const next = { level, source, ...patch };
    setLevel(next.level); setSource(next.source);
    const reset = { logs_page: 1 };
    setPages((p) => ({ ...p, ...reset }));
    run(activeParams({ ...patch, ...reset }));
  };

  const onPage = (key, n) => {
    setPages((p) => ({ ...p, [key]: n }));
    run(activeParams({ [key]: n }));
  };

  const onExport = async (range = "24h") => {
    try {
      const p = {};
      const book = bookInput.trim();
      const code = codeInput.trim();
      const order = (orderInput || "").trim();   // ← поле «Номер заказа»
      if (book) p.book = book;
      else if (order) p.order = order;           // приоритет как в поиске
      else if (code) p.code = code;
      if (level) p.level = level;
      if (source) p.source = source;
      p.range = range;
      const res = await adminApi.logsExportBlob(p);
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : "logs.json";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e.message || "Не удалось выгрузить");
    }
  };

  const copy = (text) => { try { navigator.clipboard.writeText(text); } catch (e) { /* ignore */ } };

  const counts = data?.counts || {};

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Typography level="h3">Логи клиента</Typography>

      {/* Поиск */}
      <Sheet variant="outlined" sx={{ p: 2, borderRadius: "14px" }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
          <Box>
            <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>Код техподдержки</Typography>
            <Input value={codeInput} onChange={(e) => setCodeInput(e.target.value)}
                   placeholder="000000" slotProps={{ input: { maxLength: 6, inputMode: "numeric" } }}
                   sx={{ width: 160, letterSpacing: 3 }}
                   onKeyDown={(e) => e.key === "Enter" && onSearch()} />
          </Box>
          <Typography level="body-sm" color="neutral" sx={{ pb: 1 }}>или</Typography>
          <Box>
            <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>UUID книги</Typography>
            <Input value={bookInput} onChange={(e) => setBookInput(e.target.value)}
                   placeholder="123e4567-e89b-12d3-a456-426614174000"
                   sx={{ width: 340, fontFamily: "monospace", fontSize: 13 }}
                   onKeyDown={(e) => e.key === "Enter" && onSearch()} />
          </Box>
          <Typography level="body-sm" color="neutral" sx={{ pb: 1 }}>или</Typography>
          <Box>
            <Typography level="body-xs" color="neutral" sx={{ mb: 0.25 }}>Номер заказа</Typography>
            <Input value={orderInput} onChange={(e) => setOrderInput(e.target.value)}
                   placeholder="000123"
                   sx={{ width: 160 }}
                   onKeyDown={(e) => e.key === "Enter" && onSearch()} />
          </Box>
          <Button startDecorator={<SearchRoundedIcon />} onClick={onSearch} loading={loading}>Найти</Button>
        </Box>
        <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
          По коду — всё по клиенту (книги, заказы, процессы, логи). По UUID книги — логи именно этой книги.
          Если заполнены оба поля, приоритет у UUID книги.
        </Typography>
      </Sheet>

      {error && <Alert color="danger" variant="soft">{error}</Alert>}
      {data?.not_found && data?.mode === "order" && <Alert color="warning" variant="soft">Заказ <b>{data.order}</b> не найден.</Alert>}
      {data?.not_found && data?.mode !== "order" && <Alert color="warning" variant="soft">Клиент по коду <b>{data.code}</b> не найден.</Alert>}
      {data?.book_invalid && <Alert color="warning" variant="soft">«{data.book}» не похоже на UUID книги.</Alert>}

      {data?.found && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Карточка клиента / книги */}
            {data.support && (
              <Card variant="soft" color="primary">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                  <Box>
                    <Typography level="body-xs" color="neutral">Код техподдержки</Typography>
                    <Typography level="title-lg">{data.support.code}</Typography>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" color="neutral">
                      session: {data.support.session_key || "—"} · uid: {data.support.client_uid || "—"}
                    </Typography>
                    <Typography level="body-xs" color="neutral">
                      IP: {data.support.ip || "—"} · создан {fmtDate(data.support.created_at)} · истекает {fmtDate(data.support.expires_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Tooltip title="Ссылка для входа под клиентом">
                        <IconButton
                          size="sm"
                          variant="soft"
                          onClick={() => copy(buildClientLink(data.support.user_link, linkCompress, linkConvert))}
                        >
                          <ContentCopyRoundedIcon />
                        </IconButton>
                      </Tooltip>
                      <Chip variant="outlined" color="neutral" sx={{ maxWidth: 260 }}>
                        <Typography level="body-xs" noWrap>
                          {buildClientLink(data.support.user_link, linkCompress, linkConvert)}
                        </Typography>
                      </Chip>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                      <Checkbox size="sm" label="Сжатие фото" checked={linkCompress}
                                onChange={(e) => setLinkCompress(e.target.checked)} />
                      <Checkbox size="sm" label="Конвертер HEIC" checked={linkConvert}
                                onChange={(e) => setLinkConvert(e.target.checked)} />
                    </Box>
                  </Box>
                </Box>
              </Card>
            )}
            {data.order_info && (
              <Card variant="soft" color="primary">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
                  <Box>
                    <Typography level="body-xs" color="neutral">Заказ</Typography>
                    <Typography level="title-lg">№ {data.order_info.order_number}</Typography>
                  </Box>
                  <Divider orientation="vertical" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="body-xs" color="neutral">
                      статус: {data.order_info.status} · книг: {data.order_info.books_count} · создан {fmtDate(data.order_info.created_at)}
                    </Typography>
                  </Box>
                  <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title="Скопировать ссылку">
                      <IconButton size="sm" variant="soft" onClick={() => copy(data.order_info.user_link)}>
                        <ContentCopyRoundedIcon />
                      </IconButton>
                    </Tooltip>
                    <Button size="sm" variant="solid" component="a"
                            href={data.order_info.user_link} target="_blank" rel="noreferrer">
                      Открыть книги заказа
                    </Button>
                  </Box>
                </Box>
              </Card>
            )}
            {data.book_info && (
              <Card variant="soft" color={data.book_info.exists ? "primary" : "warning"}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Typography level="body-xs" color="neutral">Логи по книге · UUID</Typography>
                  <Typography level="body-sm" sx={{ fontFamily: "monospace" }}>{data.book_info.id}</Typography>
                  {data.book_info.exists ? (
                    <Button size="sm" variant="soft" endDecorator={<OpenInNewRoundedIcon />}
                            component="a" href={data.book_info.admin_url} target="_blank">
                      Открыть книгу
                    </Button>
                  ) : (
                    <Chip size="sm" color="warning" variant="soft">книга не найдена в базе (логи показаны)</Chip>
                  )}
                </Box>
              </Card>
            )}

            {/* Счётчики */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <StatTile label="логов" value={counts.logs} color="primary" />
              <StatTile label="книг" value={counts.books} color="success" />
              <StatTile label="заказов" value={counts.orders} color="warning" />
              <StatTile label="процессов" value={counts.processes} color="neutral" />
            </Box>

            {/* Вкладки */}
            <Sheet variant="outlined" sx={{ borderRadius: "14px", overflow: "hidden" }}>
              <Tabs defaultValue={0} sx={{ bgcolor: "transparent" }}>
                <TabList sx={{ px: 1, pt: 1 }}>
                  <Tab>Логи ({counts.logs ?? 0})</Tab>
                  <Tab>Книги ({counts.books ?? 0})</Tab>
                  <Tab>Заказы ({counts.orders ?? 0})</Tab>
                  <Tab>Процессы ({counts.processes ?? 0})</Tab>
                </TabList>

                {/* Логи */}
                <TabPanel value={0}>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
                    <Select size="sm" placeholder="уровень" value={level || null}
                            onChange={(_e, v) => onFilter({ level: v || "" })} sx={{ minWidth: 130 }}>
                      <Option value="">— уровень —</Option>
                      {(data.level_choices || []).map((c) => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                    </Select>
                    <Select size="sm" placeholder="источник" value={source || null}
                            onChange={(_e, v) => onFilter({ source: v || "" })} sx={{ minWidth: 150 }}>
                      <Option value="">— источник —</Option>
                      {(data.source_choices || []).map((c) => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                    </Select>
                    <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
                      <Select size="sm" defaultValue="24h" onChange={(_e, v) => v && onExport(v)} sx={{ minWidth: 120 }}
                              startDecorator={<DownloadRoundedIcon />} placeholder="Скачать за…">
                        <Option value="1h">за 1 час</Option>
                        <Option value="6h">за 6 часов</Option>
                        <Option value="24h">за 24 часа</Option>
                        <Option value="3d">за 3 суток</Option>
                        <Option value="10d">за 10 дней</Option>
                      </Select>
                    </Box>
                  </Box>
                  <Box sx={{ overflow: "auto" }}>
                    <Table hoverRow stickyHeader sx={{ "--TableCell-height": "auto" }}>
                      <thead>
                        <tr>
                          <th style={{ width: 160 }}>Время</th>
                          <th style={{ width: 80 }}>Уровень</th>
                          <th style={{ width: 110 }}>Источник</th>
                          <th style={{ width: 160 }}>Событие</th>
                          <th>Сообщение / URL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.logs?.results || []).map((l) => <LogRow key={l.id} log={l} />)}
                        {!data.logs?.results?.length && (
                          <tr><td colSpan={5}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>логов нет</Typography></td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                  <Pager block={data.logs} onPage={(n) => onPage("logs_page", n)} />
                </TabPanel>

                {/* Книги */}
                <TabPanel value={1}>
                  <Box sx={{ overflow: "auto" }}>
                    <Table hoverRow>
                      <thead>
                        <tr><th>ID</th><th>Название</th><th>Размер</th><th>Стр.</th><th>Корзина</th><th>Создана</th><th></th></tr>
                      </thead>
                      <tbody>
                        {(data.books?.results || []).map((b) => (
                          <tr key={b.id}>
                            <td><Typography level="body-xs" sx={{ fontFamily: "monospace" }}>{short(b.id)}</Typography></td>
                            <td>{b.title}</td>
                            <td>{b.width_mm}×{b.height_mm}</td>
                            <td>{b.pages_count}</td>
                            <td>{b.in_cart ? "да" : "—"}</td>
                            <td><Typography level="body-xs">{fmtDate(b.created_at)}</Typography></td>
                            <td><IconButton size="sm" variant="plain" component="a" href={b.admin_url} target="_blank"><OpenInNewRoundedIcon /></IconButton></td>
                          </tr>
                        ))}
                        {!data.books?.results?.length && (
                          <tr><td colSpan={7}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>нет книг</Typography></td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                  <Pager block={data.books} onPage={(n) => onPage("books_page", n)} />
                </TabPanel>

                {/* Заказы */}
                <TabPanel value={2}>
                  <Box sx={{ overflow: "auto" }}>
                    <Table hoverRow>
                      <thead>
                        <tr><th>№</th><th>Статус</th><th>Сумма</th><th>Клиент</th><th>Создан</th><th></th></tr>
                      </thead>
                      <tbody>
                        {(data.orders?.results || []).map((o) => (
                          <tr key={o.id}>
                            <td>{o.order_number || short(o.id)}</td>
                            <td><Chip size="sm" variant="soft">{o.status_display}</Chip></td>
                            <td>{(Number(o.total_price) || 0).toLocaleString("ru-RU")} ₽</td>
                            <td>
                              {o.client_name} {o.client_last_name}
                              <Typography level="body-xs" color="neutral">{o.client_phone} {o.client_email}</Typography>
                            </td>
                            <td><Typography level="body-xs">{fmtDate(o.created_at)}</Typography></td>
                            <td><IconButton size="sm" variant="plain" component="a" href={o.admin_url} target="_blank"><OpenInNewRoundedIcon /></IconButton></td>
                          </tr>
                        ))}
                        {!data.orders?.results?.length && (
                          <tr><td colSpan={6}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>нет заказов</Typography></td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                  <Pager block={data.orders} onPage={(n) => onPage("orders_page", n)} />
                </TabPanel>

                {/* Процессы */}
                <TabPanel value={3}>
                  <Box sx={{ overflow: "auto" }}>
                    <Table hoverRow>
                      <thead>
                        <tr><th>Тип</th><th>Статус</th><th>Заказ</th><th>Создан</th><th>Завершён</th><th>Ошибка</th></tr>
                      </thead>
                      <tbody>
                        {(data.processes?.results || []).map((p) => (
                          <tr key={p.id}>
                            <td>{p.process_type_display}</td>
                            <td><Chip size="sm" variant="soft">{p.status_display}</Chip></td>
                            <td><Typography level="body-xs" sx={{ fontFamily: "monospace" }}>{short(p.order_id)}</Typography></td>
                            <td><Typography level="body-xs">{fmtDate(p.created_at)}</Typography></td>
                            <td><Typography level="body-xs">{p.finished_at ? fmtDate(p.finished_at) : "—"}</Typography></td>
                            <td><Typography level="body-xs" color="danger">{p.error_message || "—"}</Typography></td>
                          </tr>
                        ))}
                        {!data.processes?.results?.length && (
                          <tr><td colSpan={6}><Typography level="body-sm" color="neutral" sx={{ p: 1 }}>нет процессов</Typography></td></tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                  <Pager block={data.processes} onPage={(n) => onPage("processes_page", n)} />
                </TabPanel>
              </Tabs>
            </Sheet>
          </Box>
        </motion.div>
      )}
    </Box>
  );
}